import { useIntervalFn } from '@vueuse/core'

import { useUser } from '#User/composables/useUser'

const POLL_INTERVAL_MS = 4000
const MAX_POLL_ATTEMPTS = 30

export function useTelegramBinding() {
  const { service, store } = useUser()
  const { send, getError, clearError, isLoading: bindLoading } = useRequest()
  const checkLoading = ref(false)
  const waitingForBind = ref(false)
  const isTelegramBotAvailable = ref<boolean | null>(null)
  const pollAttempts = ref(0)
  const toast = useToast()

  const serverError = computed(() => getError())

  const isTelegramBound = computed(() =>
    Boolean(store.user?.telegram?.userId),
  )

  const telegramUserName = computed(() => store.user?.telegram?.userName?.trim())

  const { pause: pausePolling, resume: resumePolling } = useIntervalFn(
    () => checkTelegramStatus(),
    POLL_INTERVAL_MS,
    { immediate: false },
  )

  onUnmounted(() => {
    pausePolling()
  })

  async function checkTelegramBotAvailability() {
    try {
      isTelegramBotAvailable.value = await service.isTelegramBotAvailable()
    } catch {
      // If we can't verify availability, keep the UI visible instead of hiding it.
      isTelegramBotAvailable.value = true
    }
  }

  onMounted(() => {
    void checkTelegramBotAvailability()
  })

  function openTelegramLink(tgLink: string) {
    window.open(tgLink, '_blank', 'noopener,noreferrer')
  }

  function stopPolling() {
    waitingForBind.value = false
    pausePolling()
  }

  async function checkTelegramStatus(options?: { manual?: boolean }) {
    if (!options?.manual) {
      pollAttempts.value += 1

      if (pollAttempts.value > MAX_POLL_ATTEMPTS) {
        stopPolling()
        return
      }
    }

    try {
      if (options?.manual) {
        checkLoading.value = true
      }

      await service.loadCurrentUser()

      if (isTelegramBound.value) {
        stopPolling()
        clearError()
        toast.add({
          color: 'success',
          title: 'Telegram привязан',
          description: 'Аккаунт Telegram успешно привязан',
        })
      }
    } finally {
      if (options?.manual) {
        checkLoading.value = false
      }
    }
  }

  function startPolling() {
    pollAttempts.value = 0
    waitingForBind.value = true
    resumePolling()
  }

  async function handleBindTelegram() {
    try {
      clearError()
      const response = await send(() => service.bindTelegram(), {
        throwError: true,
        collectApiErrors: true,
      })

      if (!response.tgLink) {
        return
      }

      openTelegramLink(response.tgLink)
      startPolling()
    } catch (error) {
      console.error(error)
    }
  }

  async function handleCheckStatus() {
    await checkTelegramStatus({ manual: true })
  }

  return {
    serverError,
    isTelegramBound,
    telegramUserName,
    isTelegramBotAvailable,
    bindLoading,
    checkLoading,
    waitingForBind,
    handleBindTelegram,
    handleCheckStatus,
  }
}
